/**
 * Tela `/pair` — TH-54512 / M2.E5 (bootstrap pairing).
 *
 * Operador escaneia, no celular, o QR exibido pela BootstrapScreen do
 * player Android. O QR aponta para `/pair?ip=...&port=...&fp=...&...`
 * (rede local). Esta tela:
 *
 *   1) Exige login JWT (`RequireAuth`).
 *   2) Mostra os dados de hardware capturados via query string (modelo,
 *      MAC, fingerprint truncado).
 *   3) Pede nome da TV + loja do tenant logado.
 *   4) Ao confirmar:
 *      a. `POST /api/tvs` no backend cria o registro vinculado ao
 *         fingerprint do device e devolve `{tv_id, token, api_base_url}`.
 *      b. `POST http://<ip>:<port>/activate` direto na TV (rede local
 *         do operador) entrega `{token, api_base_url, name}` para ela
 *         sair da BootstrapScreen.
 *
 * Falhas tratadas em mensagens estáveis (DoD literal):
 *   - fetch local quebra → "TV não está mais alcançável, verifique a rede"
 *   - 409 do backend → "Esta TV já está cadastrada"
 *   - 403 do backend → "Você não tem permissão para parear nesse tenant"
 */

import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { z } from "zod";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";

const formSchema = z.object({
  name: z.string().min(1, "Nome obrigatório").max(120),
  store_id: z.string().min(1, "Escolha a loja"),
});
type FormData = z.infer<typeof formSchema>;

interface StoreOption {
  id: string;
  name: string;
  active?: boolean;
}

interface PagedStoreResponse {
  data: StoreOption[];
}

interface PairResponse {
  tv_id: string;
  token: string;
  api_base_url: string;
}

async function fetchStores(): Promise<StoreOption[]> {
  // per_page=100 é o limite atual do backend. TH-55119.
  const res = await fetch("/api/stores?page=1&per_page=100", {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const body = (await res.json()) as PagedStoreResponse;
  return body.data;
}

interface PairPayload {
  name: string;
  store_id: string;
  device_fingerprint: string | null;
  mac_address: string | null;
  device_model: string | null;
  android_sdk: number | null;
  local_ip: string | null;
}

async function pairOnBackend(payload: PairPayload): Promise<PairResponse> {
  const res = await fetch("/api/tvs", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err: Error & { status?: number } = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as PairResponse;
}

async function activateOnTV(
  ip: string,
  port: string,
  body: { token: string; api_base_url: string; name: string },
): Promise<void> {
  try {
    const res = await fetch(`http://${ip}:${port}/activate`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
  } catch {
    // Backend já gravou a TV — mas se o operador trocou de rede, o
    // /activate local falha. Mensagem fixa do DoD.
    throw new Error("TV não está mais alcançável, verifique a rede");
  }
}

function PairContent() {
  const search = useSearch({ from: "/pair" });
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);

  const ip = (search.ip ?? "").trim();
  const port = (search.port ?? "8765").trim() || "8765";
  const fp = (search.fp ?? "").trim();
  const model = (search.model ?? "").trim();
  const mac = (search.mac ?? "").trim();
  const sdkRaw = search.sdk ?? "";
  const sdk = useMemo(() => {
    const n = Number.parseInt(String(sdkRaw), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [sdkRaw]);
  const fpTail = fp ? fp.slice(-8) : "";

  const storesQuery = useQuery({
    queryKey: ["stores", "pair-options"],
    queryFn: fetchStores,
    staleTime: 60_000,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: "", store_id: "" },
  });

  useEffect(() => {
    reset({
      name: model ? `TV ${model}` : "",
      store_id: "",
    });
  }, [model, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormData) => {
      const pairing = await pairOnBackend({
        name: values.name,
        store_id: values.store_id,
        device_fingerprint: fp || null,
        mac_address: mac || null,
        device_model: model || null,
        android_sdk: sdk,
        local_ip: ip || null,
      });
      await activateOnTV(ip, port, {
        token: pairing.token,
        api_base_url: pairing.api_base_url,
        name: values.name,
      });
      return pairing;
    },
    onSuccess: (data) => {
      navigate({ to: "/tvs/$tvId", params: { tvId: data.tv_id } });
    },
    onError: (err: Error & { status?: number }) => {
      if (err.status === 409) {
        setServerError("Esta TV já está cadastrada");
      } else if (err.status === 403) {
        setServerError("Você não tem permissão para parear nesse tenant");
      } else {
        setServerError(err.message || "Erro inesperado ao parear");
      }
    },
  });

  const onSubmit = (values: FormData) => {
    setServerError(null);
    mutation.mutate(values);
  };

  return (
    <AppLayout
      breadcrumb="OPERAÇÃO / TELAS / PAREAR"
      title="Parear nova TV"
    >
      <div className="mx-auto w-full max-w-2xl">
        <Card className="mt-0 space-y-6">
          <section aria-label="Dados do device" className="space-y-2">
            <h2 className="text-sm font-semibold text-[var(--color-foreground)]">
              Device escaneado
            </h2>
            <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-[var(--color-muted-foreground)]">Modelo</dt>
                <dd className="font-medium">{model || "—"}</dd>
              </div>
              <div>
                <dt className="text-[var(--color-muted-foreground)]">
                  IP local da TV
                </dt>
                <dd className="font-mono">
                  {ip ? `${ip}:${port}` : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--color-muted-foreground)]">MAC</dt>
                <dd className="font-mono">{mac || "—"}</dd>
              </div>
              <div>
                <dt className="text-[var(--color-muted-foreground)]">Android SDK</dt>
                <dd>{sdk ?? "—"}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-[var(--color-muted-foreground)]">
                  Device ID (final)
                </dt>
                <dd className="font-mono">
                  {fpTail ? `…${fpTail}` : "—"}
                </dd>
              </div>
            </dl>
          </section>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            noValidate
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="pair-name">Nome da TV</Label>
              <Input
                id="pair-name"
                autoFocus
                placeholder="TV Caixa 1"
                aria-invalid={errors.name ? true : undefined}
                {...register("name")}
              />
              {errors.name ? (
                <span
                  role="alert"
                  className="text-xs text-[var(--color-destructive)]"
                >
                  {errors.name.message}
                </span>
              ) : null}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="pair-store">Loja</Label>
              <Select
                id="pair-store"
                aria-invalid={errors.store_id ? true : undefined}
                disabled={storesQuery.isLoading}
                {...register("store_id")}
              >
                <option value="">
                  {storesQuery.isLoading
                    ? "Carregando lojas…"
                    : "Escolha a loja"}
                </option>
                {(storesQuery.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
              {errors.store_id ? (
                <span
                  role="alert"
                  className="text-xs text-[var(--color-destructive)]"
                >
                  {errors.store_id.message}
                </span>
              ) : null}
              {storesQuery.isError ? (
                <span
                  role="alert"
                  className="text-xs text-[var(--color-destructive)]"
                >
                  Não foi possível carregar as lojas. Recarregue a página.
                </span>
              ) : null}
            </div>

            {!ip ? (
              <div
                role="alert"
                className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
              >
                IP da TV ausente na URL. Reabra o QR no celular.
              </div>
            ) : null}

            {serverError ? (
              <div
                role="alert"
                className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
              >
                {serverError}
              </div>
            ) : null}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate({ to: "/" })}
                disabled={isSubmitting || mutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || mutation.isPending || !ip}
              >
                {mutation.isPending ? "Pareando…" : "Parear TV"}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </AppLayout>
  );
}

export function PairTVPage() {
  return (
    <RequireAuth>
      <PairContent />
    </RequireAuth>
  );
}
